import {
  PricingRequestType,
  ServiceLevelType,
} from '@store/customers/customerDetails/LTL';
import { CarrierMaxLiability } from '@store/ltl/types';
import { Comments } from '@typeDefinitions/comments';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';

import { EquipmentCategoryT, PreferredLaneT } from '../types';

export type MaxLiabilityCustomer = {
  customerId: number | null;
  customerName: string;
};

export type CarrierFuelSurchargeDetails = {
  isDeleted: boolean | null;
  isDefault: boolean;
  eIARegion: string;
  effectiveDay: number;
  fuelSurchargeRanges: FuelSurchargeRanges[];
};

export type FuelSurchargeRanges = {
  id: string;
  min: number;
  max: number;
  surchargeModifier: number;
  type: number;
};

export type CarrierDocument = {
  dateAdded: string;
  id: number;
  name: string;
  url: string;
  taggedCategoryKeys: string[];
};

export type CarrierAdjustmentAccounts = {
  id: string;
  accountNumber: number;
  accountName: string;
  type: number;
  description: string;
  createdDate: string;
  createdBy: string;
  modifiedDate: string;
  historyContext: string;
  modifiedBy: string;
};

export type CarrierDetailsState = {
  isLoading: boolean;
  isError: boolean;
  carrierDetails: CarrierDetails | null;
  carrierDocuments: CarrierDocument[];
  carrierFuelSurcharge: CarrierFuelSurchargeDetails | null;
  carrierAdjustments: CarrierDetailsAdjustment[];
  selectedCountry: 'US' | 'CA' | 'MX';
  carrierDetailsErrors: Record<string, string[]> | null;
  creationMode: boolean;
  carrierAdjustmentAccounts: CarrierAdjustmentAccounts[];
  carrierMaxLiability: CarrierMaxLiability[];
  /** Pricing type labels the carrier supports (e.g. 'Contract'), from the liability types API. */
  carrierLiabilityTypes: string[];
  /** Customer scopes for max liability, with the synthetic Blanket entry first. */
  maxLiabilityCustomers: MaxLiabilityCustomer[];
  /** Customer scope currently loaded into carrierMaxLiability; null is Blanket. */
  maxLiabilityCustomerId: number | null;
  /** Carrier whose max liability setup is currently loaded, used to avoid refetching on tab switches. */
  maxLiabilityCarrierId: number | null;
  maxLiabilityLoading: boolean;
  /** True while max liability rows are being persisted; the editor and scope selector are disabled. */
  maxLiabilitySaving: boolean;
  preferredLanes: PreferredLaneT[];
  equipmentCategories: EquipmentCategoryT[];
  showDuplicateBanner: boolean;
};

export type CarrierLoadHistoryState = {
  isLoading: boolean;
  isError: boolean;
  items: CarrierLoadHistory[];
  totalCount: number;
};

export type CarrierReactivationRequestCacheItemT = {
  carrierId: number;
  requestedAtUtc: string;
  requestedByUserId: number;
  requestedByUserName: string;
  timeToLiveInMinutes: number | null;
  expiresAtUtc: string | null;
  comment?: string | null;
};

export type HighwayRateConRecipient = {
  name: string;
  email: string;
  phone: string;
};

export type HighwayMetadataViewModel = {
  carrierLegalName: string | null;
  carrierDbaName: string | null;
  dotNumbers: string | null;
  mcNumbers: string | null;
  highwayStatus: string | null;
  highwaySupportedModes: string | null;
  isMonitored: boolean;
  onboardingVendorExternalId: number | null;
};

export type CarrierDetails = {
  operationType: OperationTypeV4;
  overallMaxLiabilityClass: string;
  hasWatchDogAlerts: boolean;
  hasIdentityAlerts: boolean;
  highwayUrl: string | null;
  highwayMetadataViewModel?: HighwayMetadataViewModel | null;
  id: number;
  currentContactId: number;
  requireMacropointTracking: boolean | null;
  shipperVerifyMCDotNumber: boolean | null;
  nonUs: boolean;
  name: string;
  documentPath: string;
  taxId: string;
  mc: string | null;
  dot: string | null;
  billingTerms: string;
  remitTo: string | null;
  emailCopy: string | null;
  liabilityInsuranceCompany: string;
  liabilityLimit: number;
  liabilityPolicyNumber: string;
  liabilityExpiration: string;
  cargoInsuranceCompany: string;
  cargoLimit: number;
  cargoPolicyNumber: string;
  cargoExpiration: string;
  onboardingVendorCarrierExclusion:
    | OnboardingVendorCarrierExclusionViewModel
    | undefined;
  workersCompInsuranceCompany: string | null;
  workersCompLimitEachAccident: number | null;
  workersCompPolicyNumber: string | null;
  workersCompExpiration: string | null;
  workersCompLimitDisease: number | null;
  workersCompLimitPolicy: number | null;
  contractDocument: null;
  contractSignedDate: string | null;
  contractSignedBy: string | null;
  documents: string[] | null;
  needs1099: boolean;
  factor: boolean;
  conditionalActionPlanReceived: boolean;
  status: string;
  createdDate: string | null;
  modifiedDate: string | null;
  falloutCount: number;
  bounceSummary: BounceSummary;
  mode: string;
  allowAdvances: boolean;
  alreadySetup: boolean;
  accountingSysId: string | null;
  vendorHold: boolean;
  taxIDVerificationDate: string | null;
  smartWayCertified: boolean;
  reeferInsurance: boolean;
  hazmatCertified: boolean;
  safetyRating: string | null;
  safetyWarnings: null;
  brokerAuthority: boolean;
  commonAuthority: boolean;
  contractAuthority: boolean;
  isFavorite: boolean;
  officeRating: number;
  numRatings: number;
  numberOfLoads: number;
  ltlEnabled: boolean;
  comments: Comments[];
  contacts: Contact[];
  ratings: Rating[];
  rating?: number;
  equipments: CarrierEquipment[];
  lanes: CarrierDetailsLane[];
  maxLiabilities: CarrierDetailsMaxLiability[];
  overallMaxUsedLiability: number | null;
  overallMaxNewLiability: number | null;
  blacklist: null | CarrierDetailsBlacklist;
  corporatePhoneNumber: string | null;
  ltlTerminalLink: string | null;
  ltlRateNote: string | null;
  transfloEnabled: boolean;
  vendorHoldReason: string | null;
  vendorHoldDescription: string | null;
  notes: string | null;
  usaAddress: Address;
  canAddress: Address;
  mexAddress: Address;
  averageRating: number;
  paymentTermDays: number;
  hasExpiredCargoInsurance: boolean;
  hasExpiredLiabilityInsurance: boolean;
  isValid: boolean;
  overallRiskAssessment?: string;
  certifications: Certification[];
  carrierInsights: CarrierInsight[] | [];
  carrierInsurances: Insurance[];
  highValueCertified: boolean;
  operatingService: string;
  vendorAssessment: string;
  lessThanThreeLoads: boolean;
  billingContactEmail: string;
  carrierHighwayRuleAssociations: CarrierHighwayRuleAssociationViewModel[];
  intrastateFullyPassed: boolean;
  interstateFullyPassed: boolean;
  highValueCargoFullyPassed: boolean;
  refrigeratedFullyPassed: boolean;
  caRefrigeratedFullyPassed: boolean;
  hazmatFullyPassed: boolean;
  canadianCrossBorderFullyPassed: boolean;
  coBrokerFullyPassed: boolean;
  carrierReactivationRequestCacheItemViewModel: CarrierReactivationRequestCacheItemT | null;
  highwayRateConRecipients: HighwayRateConRecipient[];
  isDuplicate?: boolean;
};

export type CarrierHighwayRuleAssociationViewModel = {
  ruleCoding: string;
  display: string;
  description: string;
  passing: boolean;
  ruleCodingId: number;
};

export type CarrierInsight = {
  insightId: number;
  title: string;
  message: string;
};

export type Certification = {
  fileDate: string;
  verified: boolean;
  name: string;
};

export type Insurance = {
  id: number;
  carrierId: number;
  source: string;
  internalStatus?: string;
  insuranceType: string;
  displayFriendlyInsuranceType?: string;
  insuranceCompanyName: string;
  policyNumber: string;
  naic?: string;
  effectiveDate?: string;
  expirationDate?: string;
  cancellationDate?: string;
  atgCancellationDate?: string;
  coverageLimit?: number;
  limitType?: string;
  limitTypeDisplayFriendlyName?: string;
  externalInsuranceCertificateId?: number;
  hasAdditionalInsured?: boolean;
  hasSubrogrationWaived?: boolean;
  isRefrigerationBreakdownIncluded?: boolean;
  currencyType?: string;
  insuranceCompanyShortName?: string;
  phone?: string;
};

export type Comment = {
  id: number;
  objectId: number;
  userId: number;
  name: string;
  type: null;
  comment: string;
  createdBy: null;
  timestamp: string;
  thumbnail: string | null;
  operationType: OperationTypeV4;
};

export type Contact = {
  verificationStatus: number;
  emailVerified: boolean;
  phoneVerified: boolean;
  id: number;
  objectId: number;
  objectType: string | null;
  name: string;
  phone1: string | null;
  extension1: string | null;
  phone2: string | null;
  extension2: string | null;
  fax: string | null;
  email: string | null;
  type: string | null;
  status: string | null;
  createdDate: string | null;
  modifiedDate: string | null;
  mailerEmails: boolean;
  password: null;
  userName: null;
  createdBy: null;
  modifiedBy: null;
  phone: string | null;
  verifiedDisabled: boolean | null;
  ownershipSource: number | null;
  customerPortalPassword?: string | null;
  operationType: OperationTypeV4;
};

export type Rating = {
  id: number;
  objectId: number;
  rating: number;
  commentId: number;
  loadId: number;
  comment: Comment;
  operationType: OperationTypeV4;
};

export type BounceReason = {
  reasonId: number;
  code: string;
  name: string;
  sortOrder: number;
  count: number;
};

export type BounceSummary = {
  reasons: BounceReason[];
  hauledLoads: number;
  totalBounces: number;
  totalAssignments: number;
  bouncePercentage: number;
  windowMonths: number;
};

export type Address = {
  operationType: OperationTypeV4;

  id: number;
  enabled: boolean;
  countryCode: string;
  physicalAddress1: string | null;
  physicalAddress2: string | null;
  physicalCity: string | null;
  physicalZip: string | null;
  physicalStateOrProvince: string | null;
  mailingAddress1: string | null;
  mailingAddress2: string | null;
  mailingCity: string | null;
  mailingStateOrProvince: string | null;
  mailingZip: string | null;
  factoringCompanyId: number | null;
  achRouting: string | null;
  achAccount: string | null;
  factoringCompany: FactoringCompany | null;
};

export type FactoringCompany = {
  operationType: OperationTypeV4;
  id: number;
  createdDate: string | null;
  modifiedDate: string | null;
  name: string;
  taxId: string | null;
  needs1099: boolean;
  vendorHold: boolean;
  physicalAddress1: string | null;
  physicalAddress2: string | null;
  physicalCity: string | null;
  physicalState: string | null;
  physicalCountryCode: string | null;
  physicalZip: string | null;
  remitTo: string;
  mailingAddress1: string | null;
  mailingAddress2: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingCountryCode: string | null;
  mailingZip: string | null;
  billingTerms: string;
  status: string;
  userName: string | null;
  password: string | null;
  accountingSysId: string | null;
  achAccount: string | null;
  achRouting: string | null;
  primaryContact: Contact;
  carriers: Carrier[];
  isCarrierExist: boolean;
  comments: Comments[];
  contacts: Contact[] | null;
  numberOfCarriers: number;
};

export type Carrier = {
  operationType: OperationTypeV4;
  id: number;
  name: string;
  mc: string;
  dot: string;
  taxId: string;
  billingTerms: string;
  physicalAddress1: string;
  physicalAddress2: string | null;
  physicalCity: string;
  physicalState: string;
  physicalZip: string;
  mailingAddress1: string;
  mailingAddress2: string | null;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  achRouting: string | null;
  achAccount: string | null;
  remitTo: null;
  liabilityInsuranceCompany: string;
  liabilityLimit: number;
  liabilityPolicyNumber: string;
  liabilityExpiration: string;
  cargoInsuranceCompany: string;
  cargoLimit: number;
  cargoPolicyNumber: string;
  cargoExpiration: string;
  contractDocument: null;
  contractSignedDate: null;
  contractSignedBy: null;
  documentsFolder: null;
  needs1099: boolean;
  factor: boolean;
  status: string;
  createdDate: string;
  modifiedDate: string;
  falloutCount: number;
  mode: null;
  allowAdvances: boolean;
  accountingSysId: string;
  vendorHold: boolean;
  taxIDVerificationDate: null;
  smartWayCertified: boolean;
  hazmatCertified: boolean;
  reeferInsurance: boolean;
  workersCompInsuranceCompany: null;
  workersCompLimitEachAccident: null;
  workersCompPolicyNumber: null;
  workersCompExpiration: null;
  workersCompLimitDisease: null;
  workersCompLimitPolicy: null;
  isVendor: null;
  ltlEnabled: boolean;
  conditionalActionPlanReceived: boolean;
  factoringCompanyId: number;
};

export type CarrierEquipment = {
  id: number;
  category: string;
  name: string;
  datEquipment: string | null;
  createdDate: string | null;
  createdBy: string | null;
  operationType: OperationTypeV4;
};

export type CarrierLoadHistory = {
  orderId: number;
  loadNumber: string;
  agent: string;
  carrier: string;
  carrierPay: number | null;
  origin: string;
  destination: string | null;
  pickUp: string;
  dropOff: string;
  equipment: string;
  orderLoadSeq: number;
  status: string;
  mode: string;
  myOffice: boolean;
  stops: string;
};

export type CarrierDetailsAdjustment = {
  id: string;
  userId: number;
  account: number | null;
  accountName: string;
  amount: number;
  description: string;
  createdDate: string;
  settledDate: string | null;
  status: string;
  draftPaymentKey: string;
  carrierId: number;
  loadNumber: string;
  carrierAdjustmentPayments: CarrierAdjustmentPayment[];
};

export type CarrierAdjustmentPayment = {
  id: number;
  paymentDate: string;
  amount: number;
};

export type CarrierDetailsAdjustmentAutoComplete = {
  orderId: number;
  loadNumber: string;
  agent: string;
  carrier: string;
  carrierPay: number;
  origin: string;
  destination: string;
  pickUp: string;
  dropOff: string;
  equipment: string;
  orderLoadSeq: number;
  status: string;
  mode: string;
  type: null | string;
  myOffice: boolean;
};

export type CarrierDetailsClaimHistoryLoad = {
  id: number;
  parentLoadId: number;
  loadId: number;
  holdAmount: number;
  child: boolean;
  orderId: number;
  orderLoadSeq: number;
  status: string;
  holdDateUtc: string;
  holdReleaseDateUtc: string | null;
  releaseComment: string | null;
  releaseByUser: string | null;
  user: string;
  office: string;
};

export type CarrierDetailsClaimHistory = {
  orderId: number;
  loadNumber: string;
  agent: string;
  carrier: string;
  claimDate: string;
  description: string;
  claimNumber: string;
  value: number;
  resolution: string | null;
  closeDate: string | null;
  cause: string | null;
  status: string;
  holdStatus?: string;
  claimLoads: CarrierDetailsClaimHistoryLoad[];
};

export type ClaimReleaseViewModel = {
  carrierId: number;
  loadId: number;
  parentLoadId: number;
  status: 'Released';
  releaseDate?: string;
  releasedBy?: string;
  releaseComments?: string;
};

export type CarrierDetailsBlacklist = {
  id: number;
  carrierId: number;
  officeId: number;
  bannedDate: string;
  comment: string;
  bannedBy: string;
};

export type CarrierDetailsLane = {
  operationType: OperationTypeV4;
  id: number;
  name: string;
  longName: string;
};

export type CarrierDetailsMaxLiability = {
  id: string | null;
  class: string;
  maxUsed: number | null;
  maxNew: number | null;
  operationType?: OperationTypeV4;
};

export type OnboardingVendorCarrierExclusionViewModel = {
  id: number;
  carrierId: number;
  reason: string | undefined;
  isIgnored: boolean;
  overrideExpiration: string | undefined;
  operationType: OperationTypeV4;
};

export type CarrierLoadHistoryFilterType =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'GREATER_THAN'
  | 'GREATER_THAN_OR_EQUALS'
  | 'LESS_THAN'
  | 'LESS_THAN_OR_EQUALS'
  | 'CONTAINS'
  | 'NOT_CONTAINS'
  | 'STARTS_WITH'
  | 'ENDS_WITH';

export interface CarrierLoadHistoryFilter {
  field: string;
  filterType: CarrierLoadHistoryFilterType;
  values: (string | number)[];
}

interface Sort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface LoadHistoryPayload {
  filters: CarrierLoadHistoryFilter[];
  sort: Sort;
  pageNumber: number;
  pageSize: number;
}

export type CarrierDetailLoadHistoryResponse = {
  data: CarrierLoadHistory[];
  totalCount: number;
};

export type HighwayRuleCategoryViewModel = {
  id: number;
  internalCode: string;
  name: string;
  ruleCodings: HighwayRuleCodingViewModel[];
};

export type HighwayRuleCodingViewModel = {
  id: number;
  display: string;
  description: string;
};

export type CarrierScac = {
  carrierName: string;
  carrierId: number;
  carrierScacId: number;
  scac: string;
  trackingUrl: string | null;
  supportsUsa: boolean;
  supportsCanada: boolean;
  supportsMexico: boolean;
};

/** Which countries a SCAC is allowed to quote. Persisted as flags on the backend. */
export type CountrySupport = Pick<
  CarrierScac,
  'supportsUsa' | 'supportsCanada' | 'supportsMexico'
>;

/**
 * What a SCAC supports when we have no information yet: USA and Canada, but not Mexico.
 * Mexico is opt-in because it needs specific carrier capability. The frontend owns this
 * default — the database stores only what it is given.
 */
export const DEFAULT_COUNTRY_SUPPORT: CountrySupport = {
  supportsUsa: true,
  supportsCanada: true,
  supportsMexico: false,
};

export interface CarrierCSPAccount {
  id: number;
  customerName: string | null;
  customerId: number | null;
  carrierName: string;
  carrierId: number;
  carrierScacId: number;
  scac: string;
  termTypeId: number;
  termTypeDescription: string;
  payerTypeId: number;
  payerTypeDescription: string;
  evaAccessId: string | null;
  accountNumber: string;
  billToName: string | null;
  serviceLevelTypes?: ServiceLevelType[];
  pricingRequestTypes?: PricingRequestType[];
}

export type CarrierCSPCustomersState = {
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  cspCustomers: CarrierCSPAccount[];
  cspEnabledCustomers: CSPEnabledCustomerLookup[];
};
export interface CSPEnabledCustomerLookup extends Record<string, unknown> {
  name: string;
  status: string;
  customerId: number;
}

export type AddCarrierContactPayload = {
  name: string;
  type: string;
  phone1: string;
  phoneVerified: boolean;
  extension1: string | null;
  phone2: string;
  extension2: string | null;
  fax: string | null;
  email: string;
  emailVerified: boolean;
  cpn: string | null;
  userName: string | null;
  password: string | null;
  objectId: number;
};

export type ParentClaimHoldViewModelT = {
  carrierId: number;
  loadId?: number | null;
  maxHoldAmount?: number | null;
  targetHoldAmount?: number | null;
  claimAmount?: number | null;
  loadStatus?: string | null;
  orderId: number;
  orderLoadSeq: number;
  reason?: string | null;
  description?: string | null;
  claimReferenceNumber?: string | null;
  office?: string | null;
  pickupDateTime?: string | null;
  dropOffDateTime?: string | null;
};

export type ClaimLoadHoldViewModelT = {
  parentLoadId: number;
  carrierId: number;
  loadId: number;
  maxHoldAmount?: number | null;
  targetHoldAmount?: number | null;
  holdAmount?: number | null;
  loadStatus?: string | null;
  orderId: number;
  orderLoadSeq: number;
  reason?: string | null;
  office?: string | null;
  pickupDateTime?: string | null;
  dropOffDateTime?: string | null;
};

export type CarrierPaymentHoldsViewModelT = {
  parentClaimHoldViewModel: ParentClaimHoldViewModelT;
  claimLoadHoldViewModels: ClaimLoadHoldViewModelT[];
};
